import emailjs from '@emailjs/browser';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Initialize EmailJS with public key
if (PUBLIC_KEY) {
  emailjs.init(PUBLIC_KEY);
}

/**
 * Send order notification email to company
 * @param {Object} orderData - Complete order data
 * @returns {Promise} - EmailJS response
 */
export const sendOrderNotificationEmail = async (orderData) => {
  try {
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      throw new Error('EmailJS credentials not configured. Please check .env file.');
    }

    const { customer, items, subtotal, shipping, total, paymentMethod } = orderData;

    // Format order items for email
    let orderItemsText = '';
    items.forEach((item, index) => {
      orderItemsText += `${index + 1}. ${item.name}\n`;
      orderItemsText += `   Quantity: ×${item.quantity}\n`;
      orderItemsText += `   Price: Rs ${typeof item.price === 'string' ? item.price : item.price.toLocaleString()} each\n`;
      orderItemsText += `   Subtotal: Rs ${(item.price * item.quantity).toLocaleString()}\n`;
      
      if (item.prescription) {
        const prescription = formatPrescriptionDetails(item.prescription, item.productType);
        if (prescription !== 'None') {
          orderItemsText += `   Prescription:\n${prescription}\n`;
        }
      }
      orderItemsText += '\n';
    });

    // Format delivery address
    let deliveryAddress = customer.address;
    if (customer.apartment) {
      deliveryAddress += `, ${customer.apartment}`;
    }
    
    // Format date
    const orderDate = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Prepare template parameters
    const templateParams = {
      customer_name: `${customer.firstName} ${customer.lastName}`,
      customer_phone: customer.phone,
      customer_contact: customer.contact || 'N/A',
      delivery_address: deliveryAddress,
      city: customer.city,
      postal_code: customer.postalCode || 'N/A',
      country: customer.country,
      order_items: orderItemsText,
      subtotal: `Rs ${subtotal.toLocaleString()}`,
      shipping: `Rs ${shipping.toLocaleString()}`,
      total: `Rs ${total.toLocaleString()}`,
      payment_method: paymentMethod || 'Cash on Delivery',
      order_date: orderDate,
      order_number: `ORD-${Date.now()}`
    };

    // Send email
    const response = await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      templateParams
    );

    console.log('Email sent successfully:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Failed to send email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Format prescription details for email
 */
const formatPrescriptionDetails = (prescription, productType) => {
  if (!prescription) return 'None';

  const { rightEye, leftEye } = prescription;
  let hasRightPrescription, hasLeftPrescription;
  
  if (productType === 'lenses') {
    hasRightPrescription = rightEye?.distance?.sph;
    hasLeftPrescription = leftEye?.distance?.sph;
  } else {
    hasRightPrescription = rightEye?.distance?.sph || rightEye?.distance?.cyl || rightEye?.distance?.axis || 
                          rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis;
    hasLeftPrescription = leftEye?.distance?.sph || leftEye?.distance?.cyl || leftEye?.distance?.axis || 
                         leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis;
  }
  
  if (!hasRightPrescription && !hasLeftPrescription) return 'None';

  let prescriptionText = '';
  
  if (hasRightPrescription) {
    prescriptionText += 'Right Eye:\n';
    if (rightEye?.distance?.sph || (productType === 'eyeglasses' && (rightEye?.distance?.cyl || rightEye?.distance?.axis))) {
      if (productType === 'lenses') {
        prescriptionText += `  Distance: SPH: ${rightEye.distance.sph || 'null'}\n`;
      } else {
        prescriptionText += `  Distance: SPH: ${rightEye.distance.sph || 'null'}, CYL: ${rightEye.distance.cyl || 'null'}, AXIS: ${rightEye.distance.axis || 'null'}\n`;
      }
    }
    if (productType === 'eyeglasses' && (rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis)) {
      prescriptionText += `  Near: SPH: ${rightEye.near.sph || 'null'}, CYL: ${rightEye.near.cyl || 'null'}, AXIS: ${rightEye.near.axis || 'null'}\n`;
    }
  }

  if (hasLeftPrescription) {
    prescriptionText += 'Left Eye:\n';
    if (leftEye?.distance?.sph || (productType === 'eyeglasses' && (leftEye?.distance?.cyl || leftEye?.distance?.axis))) {
      if (productType === 'lenses') {
        prescriptionText += `  Distance: SPH: ${leftEye.distance.sph || 'null'}\n`;
      } else {
        prescriptionText += `  Distance: SPH: ${leftEye.distance.sph || 'null'}, CYL: ${leftEye.distance.cyl || 'null'}, AXIS: ${leftEye.distance.axis || 'null'}\n`;
      }
    }
    if (productType === 'eyeglasses' && (leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis)) {
      prescriptionText += `  Near: SPH: ${leftEye.near.sph || 'null'}, CYL: ${leftEye.near.cyl || 'null'}, AXIS: ${leftEye.near.axis || 'null'}\n`;
    }
  }

  return prescriptionText || 'None';
};

const emailService = {
  sendOrderNotificationEmail
};

export default emailService;

